import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { resolveGoogleOAuthCallback } from "../auth/oauthFlow";
import {
  getDeletedAccountAuthMessage,
  isDeletedAccountAuthError,
} from "../auth/deletedAccountMessage";
import "../styles/pages/OAuth.css";

function getCallbackErrorMessage(t, detail) {
  if (isDeletedAccountAuthError(detail)) {
    return getDeletedAccountAuthMessage(t);
  }

  if (detail === "supabase_oauth_not_configured") {
    return t("oauth_not_configured", {
      defaultValue: "Google sign-in is not configured yet.",
    });
  }

  if (detail === "oauth_provider_not_allowed") {
    return t("oauth_provider_not_allowed", {
      defaultValue:
        "This Google sign-in could not be used. Please try email login.",
    });
  }

  return t("oauth_callback_error", {
    defaultValue: "We could not finish Google sign-in. Please try again.",
  });
}

export default function AuthCallback({ setUser }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function finishOAuthLogin() {
      try {
        const result = await resolveGoogleOAuthCallback();
        if (!isMounted) return;

        if (result.profileRequired) {
          navigate("/auth/complete-profile", { replace: true });
          return;
        }

        setUser(result.user);
        navigate("/", { replace: true });
      } catch (error) {
        if (!isMounted) return;
        setErrorMessage(
          getCallbackErrorMessage(t, error?.detail || error?.message)
        );
      }
    }

    finishOAuthLogin();

    return () => {
      isMounted = false;
    };
  }, [navigate, setUser, t]);

  return (
    <div className="oauth-page">
      <div className="oauth-card">
        <h1 className="oauth-title">
          {t("oauth_callback_title", { defaultValue: "Google sign-in" })}
        </h1>

        {errorMessage ? (
          <>
            <p className="oauth-error">{errorMessage}</p>
            <Link to="/login" className="oauth-secondary-link">
              {t("back_to_login", { defaultValue: "Back to login" })}
            </Link>
          </>
        ) : (
          <p className="oauth-status">
            {t("oauth_callback_loading", {
              defaultValue: "Finishing Google sign-in...",
            })}
          </p>
        )}
      </div>
    </div>
  );
}
